'use client';

/**
 * Where a step happens — asked where the step is designed.
 *
 * The step model is the richest description of how an organization works: 319
 * steps, 301 with an assignee role, 8 step types. None of it reaches anybody
 * until a step says which PAGE it happens on. The agent cannot answer "how do
 * I ..." from it, and a new starter cannot follow it.
 *
 * So these three fields sit in the step form rather than in a tidy-up screen
 * somewhere else. The person designing the step already knows the answer;
 * asking later means asking twice, and the second ask never happens.
 *
 * WHY A REGISTRY PICKER AND NOT A TEXT BOX
 *
 * A mistyped page_key is invisible. It fails `can_open_page` for everybody, so
 * the step silently never appears anywhere — no error, no empty state, nothing
 * to notice. The options come from `page_capabilities` so that cannot happen.
 *
 * Note this is NOT the same thing as the existing NavigationPagePicker, which
 * offers a hardcoded list of ROUTES for navigate-type step config. A route is a
 * string nothing can traverse: it rots silently when the route changes, and it
 * cannot be permission-checked. A page_key can be both.
 *
 * WHY THE LIST IS NOT FILTERED TO WHAT THE AUTHOR CAN OPEN
 *
 * This is a design surface. Somebody building a workflow for caseworkers may
 * not be a caseworker, and hiding the pages they cannot personally open would
 * make the workflow undescribable. Reach is enforced where steps are READ — the
 * endpoint says so too, because the absence reads as a bug until you know why.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, X } from 'lucide-react';

import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select';
import { workflowsAPI, WorkflowPageOption } from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';

export interface StepPlacement {
  page_key?: string | null;
  location_hint?: string | null;
  is_user_facing?: boolean;
  step_group?: string | null;
}

interface StepPlacementFieldsProps {
  value: StepPlacement;
  onChange: (next: StepPlacement) => void;
  /** Section headings already used in this workflow, offered as suggestions so
   *  a second step lands in the SAME section rather than a near-miss spelling
   *  that splits one section into two. */
  existingGroups?: string[];
}

export const StepPlacementFields: React.FC<StepPlacementFieldsProps> = ({
  value,
  onChange,
  existingGroups = [],
}) => {
  const { t } = useAppTranslation();
  const [pages, setPages] = useState<WorkflowPageOption[]>([]);
  const [pagesFailed, setPagesFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    workflowsAPI
      .listPageOptions()
      .then((rows) => {
        if (!cancelled) setPages(rows);
      })
      .catch(() => {
        // A failed registry load must not block saving the step. The author
        // keeps every other field; they are told the picker is unavailable
        // rather than shown an empty dropdown that looks like "no pages exist".
        if (!cancelled) setPagesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options: SearchableSelectOption[] = useMemo(
    () =>
      pages.map((p) => ({
        value: p.page_key,
        label: p.page_name,
        group: p.section ?? t('workflows.placement.ungrouped', 'Other'),
        // The key itself, because two pages can share a display name and the
        // key is what actually gets stored.
        hint: p.page_key,
      })),
    [pages, t]
  );

  const isUserFacing = value.is_user_facing !== false;

  const set = (patch: Partial<StepPlacement>) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('workflows.placement.heading', 'Where this step happens')}
        </span>
      </div>

      {/* Whether anybody performs this on a screen at all.
          First, because it decides whether the rest of the box applies. */}
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={isUserFacing}
          onChange={(e) => set({ is_user_facing: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {t('workflows.placement.userFacing', 'Somebody performs this on a screen')}
          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
            {t(
              'workflows.placement.userFacingHelp',
              'Turn this off for steps nobody does by hand — an automated approval, a data import. They are then left out of the unmapped count instead of sitting in it forever.'
            )}
          </span>
        </span>
      </label>

      {isUserFacing && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('workflows.placement.page', 'Page')}
            </label>
            {pagesFailed ? (
              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t(
                  'workflows.placement.pagesUnavailable',
                  'The page list could not be loaded. The rest of the step still saves; set the page next time you edit it.'
                )}
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <SearchableSelect
                    options={options}
                    value={value.page_key ?? ''}
                    onValueChange={(page_key) => set({ page_key })}
                    placeholder={t('workflows.placement.pagePlaceholder', 'Search for a page...')}
                    searchPlaceholder={t('workflows.placement.pageSearch', 'Type a page name...')}
                    emptyText={t('workflows.placement.noPages', 'No page matches that')}
                  />
                </div>
                {value.page_key ? (
                  <button
                    type="button"
                    onClick={() => set({ page_key: '' })}
                    title={t('workflows.placement.clearPage', 'Clear the page')}
                    className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'workflows.placement.pageHelp',
                'Chosen from the page list so the step cannot point at a page that does not exist. It does not grant access — whoever follows the step still needs permission to open the page.'
              )}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('workflows.placement.locationHint', 'Where on that page')}
            </label>
            <input
              type="text"
              value={value.location_hint ?? ''}
              onChange={(e) => set({ location_hint: e.target.value })}
              placeholder={t('workflows.placement.locationHintPlaceholder', 'e.g. the Conversations tab')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'workflows.placement.locationHintHelp',
                'Optional. The difference between a step somebody can follow and one that sends them hunting.'
              )}
            </p>
          </div>
        </div>
      )}

      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('workflows.placement.section', 'Section')}
        </label>
        <input
          type="text"
          list="workflow-step-groups"
          value={value.step_group ?? ''}
          onChange={(e) => set({ step_group: e.target.value })}
          placeholder={t('workflows.placement.sectionPlaceholder', 'e.g. Kickoff — Paperwork')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
        {/* Suggestions rather than a fixed list: a section is free text on
            purpose, because the grouping is presentational and must not
            constrain what a step can be reused in. */}
        <datalist id="workflow-step-groups">
          {existingGroups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t(
            'workflows.placement.sectionHelp',
            'Optional. Groups a long workflow into readable parts — adopted onboarding plans arrive already grouped by phase.'
          )}
        </p>
      </div>
    </div>
  );
};

export default StepPlacementFields;
